import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { loadTimeslotConfig, getReminderTimes } from "@/hooks/usePractice";

export type NotificationStatus =
  | "loading"
  | "unsupported"
  | "denied"
  | "subscribed"
  | "unsubscribed";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || "";

// Reminder times are derived from the timeslot config (set in Zeiten settings)
function getDefaultTimes(): string[] {
  const times = getReminderTimes(loadTimeslotConfig());
  return times.length > 0 ? times : ["06:00", "09:00", "12:00", "15:00", "18:00", "21:00"];
}

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

export function useNotifications() {
  const { user } = useAuth();
  const [status, setStatus] = useState<NotificationStatus>("loading");
  const [notificationTimes, setNotificationTimes] =
    useState<string[]>(getDefaultTimes);
  const [isLoading, setIsLoading] = useState(false);

  // Check initial status
  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (!user) {
      setStatus("unsubscribed");
      return;
    }

    // Check Supabase for existing subscription
    supabase
      .from("push_subscriptions")
      .select("id, notification_times")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setStatus("subscribed");
          if (data.notification_times) {
            setNotificationTimes(data.notification_times);
          }
        } else {
          setStatus("unsubscribed");
        }
      });
  }, [user]);

  const subscribe = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      // Check if VAPID key is configured
      if (!VAPID_PUBLIC_KEY) {
        console.warn(
          "VITE_VAPID_PUBLIC_KEY not set. Push notifications require VAPID configuration."
        );
        // Still allow subscribing for UI purposes, but warn
      }

      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        setStatus("denied");
        return;
      }

      const reg = await navigator.serviceWorker.ready;

      if (VAPID_PUBLIC_KEY) {
        const sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });

        await supabase.from("push_subscriptions").upsert({
          user_id: user.id,
          subscription: sub.toJSON(),
          notification_times: getDefaultTimes(),
        });
      } else {
        // No VAPID key: save subscription record without push subscription data
        await supabase.from("push_subscriptions").upsert({
          user_id: user.id,
          subscription: {},
          notification_times: getDefaultTimes(),
        });
      }

      setNotificationTimes(getDefaultTimes());
      setStatus("subscribed");
    } catch (err) {
      console.error("Subscribe failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const unsubscribe = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      await sub?.unsubscribe();
      await supabase
        .from("push_subscriptions")
        .delete()
        .eq("user_id", user.id);
      setStatus("unsubscribed");
    } catch (err) {
      console.error("Unsubscribe failed:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const updateTimes = useCallback(
    async (times: string[]) => {
      if (!user) return;
      setNotificationTimes(times);
      await supabase
        .from("push_subscriptions")
        .update({ notification_times: times })
        .eq("user_id", user.id);
    },
    [user]
  );

  return {
    status,
    notificationTimes,
    isLoading,
    subscribe,
    unsubscribe,
    updateTimes,
  };
}
